import React from 'react';
import moment from 'moment';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
    Font
} from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';

// Importe as fontes localmente
import RobotoRegular from './fonts/Roboto-Regular.ttf';
import RobotoMedium from './fonts/Roboto-Medium.ttf';
import RobotoBold from './fonts/Roboto-Bold.ttf';
import DrSugiyama from './fonts/DrSugiyama-Regular.ttf';

// Importar fonte para símbolos
import NotoSansSymbols from './fonts/NotoSansSymbols-Regular.ttf';


// Registrar fontes
Font.register({
    family: 'Roboto',
    fonts: [
        { src: RobotoRegular, fontWeight: 400 },
        { src: RobotoMedium, fontWeight: 500 },
        { src: RobotoBold, fontWeight: 700 }
    ]
});

Font.register({
    family: 'DrSugiyama',  // Mudou de 'Dr Sugiyama' para 'DrSugiyama' (sem espaço)
    src: DrSugiyama,
    fontWeight: 'normal',
    fontStyle: 'normal'
});

Font.register({
    family: 'NotoSansSymbols',
    src: NotoSansSymbols
});

// Estilos
const signatureTextStyle = {
    fontFamily: 'DrSugiyama',
    fontSize: 24,
    height: 32,
    marginVertical: 4,
    textAlign: 'center',
    color: '#225968'
};

const styles = StyleSheet.create({
    page: {
        padding: 10,
        fontFamily: 'Roboto',
        fontSize: 10
    },
    reportContainer: {
        maxWidth: '100%',
        margin: '0 auto'
    },
    reportHeader: {
        backgroundColor: '#567a8d',
        borderBottomLeftRadius: 3,
        borderBottomRightRadius: 3,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 8,
        minHeight: 50,
        marginBottom: 2
    },
    logoOverview: {
        maxWidth: 120,
        maxHeight: 40
    },
    title: {
        color: 'white',
        fontSize: 10,
        alignSelf: 'flex-end'
    },
    reportContent: {
        flexDirection: 'column',
        gap: 4
    },
    reportTable: {
        marginBottom: 0,
        width: '100%',
        backgroundColor: 'white'
    },
    reportTableTitle: {
        backgroundColor: '#567a8d',
        padding: 4,
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
    },
    reportTableTitleText: {
        color: 'white',
        fontSize: 8,
        fontWeight: 500
    },
    tableCell: {
        padding: 4,
        fontSize: 8,
        borderWidth: 1,
        borderColor: '#ddd'
    },
    tableLabelCell: {
        width: '30%',
        color: '#225968'
    },
    tableValueCell: {
        width: '20%'
    },
    evidencesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        padding: 4,
        gap: 4
    },
    evidenceItem: {
        width: '16%'
    },
    evidenceImage: {
        width: '100%',
        height: 80,
        objectFit: 'cover',
        borderRadius: 5
    },
    evidenceTitle: {
        fontSize: 6,
        marginTop: 2,
        color: '#225968',
        textAlign: 'center',
        color: 'black',
    },
    signatureSection: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        padding: 4,
        gap: 4,
        alignContent: 'center',
    },
    signatureBlock: {
        width: '23%',
        padding: 4
    },
    signatureTitle: {
        color: '#225968',
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 8,
        textAlign: 'center'
    },
    signature: {
        ...signatureTextStyle
    },
    signatureLine: {
        borderTopWidth: 2,
        borderColor: '#225968',
        width: '90%',
        marginVertical: 8,
        alignSelf: 'center'
    },
    signatureName: {
        color: '#225968',
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'center'
    },
    signatureEmail: {
        color: '#225968',
        fontSize: 6,
        textAlign: 'center'
    },
    signatureCompany: {
        color: '#225968',
        fontSize: 6,
        textAlign: 'center'
    }
});

// Full-width text area box styles
styles.fullWidthBox = {
    borderWidth: 1,
    borderColor: '#ddd',
    borderTopWidth: 0,
    padding: 6,
    minHeight: 60,
};

const ReportTemplate = ({ report, logoUrl }) => {
    // Normalize report code to avoid issues with casing/whitespace
    const reportCode = (report?.code ?? '').toString().trim().toUpperCase();
    // Report number incl. revision, e.g. RTCB-SGRUTB09-0010-R00.
    // report.report.revision is an explicitly issued revision counter; absent means initial issue (R00).
    const reportNumber = `${report.code}-${report.subject?.location?.name ?? 'N/A'}-${report.sequential.toString().padStart(4, "0")}-R${String(report.report?.revision ?? 0).padStart(2, '0')}`;
    console.log(report)
    const { t, i18n } = useTranslation();

    // Componentes auxiliares
    const ValueText = ({ value }) => {
        const parts = String(value).split(/(Ω)/);
        return (
            <Text>
                {parts.map((part, i) =>
                    part === 'Ω' ? (
                        <Text key={i} style={{ fontFamily: 'NotoSansSymbols' }}>Ω</Text>
                    ) : (
                        <Text key={i}>{part}</Text>
                    )
                )}
            </Text>
        );
    };

    const TableRow = ({ content, columns = 2 }) => {
        const labelWidth = `${60 / columns}%`;
        const valueWidth = `${40 / columns}%`;
        return (
            <View style={{ flexDirection: 'row' }}>
                {content.map((info, index) => (
                    <React.Fragment key={index}>
                        <View style={[styles.tableCell, styles.tableLabelCell, { width: labelWidth }]}>
                            <Text>{info.title}</Text>
                        </View>
                        <View style={[styles.tableCell, styles.tableValueCell, { width: valueWidth }]}>
                            <Text style={{ color: info.valid ? 'black' : '#FF4D4F' }}>
                                {info.value !== null && typeof info.value === 'object'
                                    ? <ValueText value={Object.values(info.value)
                                        .map((value) => value.value)
                                        .join(' ')} />
                                    : <ValueText value={info.value === null ? '' : info.value} />
                                }
                            </Text>
                            <Text style={{ color: info.valid ? 'black' : '#FF4D4F', fontFamily: 'NotoSansSymbols' }}>{!info.valid ? ' ⚠' : ''}</Text>
                        </View>
                    </React.Fragment>
                ))}
            </View>
        );
    };

    const ReportTable = ({ title, content, columns = 2 }) => (
        <View style={styles.reportTable}>
            <View style={styles.reportTableTitle}>
                <Text style={styles.reportTableTitleText}>{title}</Text>
            </View>
            <View>
                {Array.from({ length: Math.ceil(content.length / columns) }).map((_, rowIndex) => {
                    const start = rowIndex * columns;
                    const end = start + columns;
                    return (
                        <TableRow
                            key={rowIndex}
                            content={content.slice(start, end)}
                            columns={columns}
                        />
                    );
                })}
            </View>
        </View>
    );

    const Evidences = ({ images }) => {
        return (
            <View style={styles.reportTable}>
                <View style={styles.reportTableTitle}>
                    <Text style={styles.reportTableTitleText}>{t('evidences')}</Text>
                </View>
                <View style={styles.evidencesGrid}>
                    {images.map((image, index) => (
                        <View key={index} style={styles.evidenceItem}>
                            <Image
                                src={image.url}
                                style={styles.evidenceImage}
                            />
                            <Text style={styles.evidenceTitle}>
                                {image.title?.toUpperCase()}
                            </Text>
                        </View>
                    ))}
                </View>
            </View>
        );
    };

    // New component: full-width textarea-like section
    const FullWidthSection = ({ title, text }) => (
        <View style={styles.reportTable}>
            <View style={styles.reportTableTitle}>
                <Text style={styles.reportTableTitleText}>{title}</Text>
            </View>
            <View style={styles.fullWidthBox}>
                <Text>{text}</Text>
            </View>
        </View>
    );

    // Funções auxiliares

    // Using report.phase directly as the data source for phases

    const formatName = (name) => {
        if (!name) return null;
        if (name.length <= 14) return name;
        const split_name = name.split(' ');
        return split_name.map((part, index) => (index === 0 ? part : `${part.charAt(0)}.`)).join(' ');
    };


    const generateContentPhases = (report) => {
        return report.phase
            .map((phase) => {
                if (phase.type === 'form') return renderFormPhase(phase);
                if (phase.type === 'camera') return renderCameraPhase(phase);
                return null;
            })
            .filter(Boolean);
    };

    const generatePendenciesTable = (report) => {
        const pendencies = [];

        const isTargetSection = (title) => {
            if (!title) return false;

            // Inclui "Perguntas Gerais" sempre
            if (title === t('general_checklist_questions')) return true;

            // Busca por seções que comecem com "Perguntas Específicas do" ou "Preguntas Específicas del"
            return title.startsWith('Perguntas Específicas do') ||
                title.startsWith('Preguntas Específicas del');
        };

        report.phase.forEach((phase) => {
            if (phase.type === 'form' && isTargetSection(phase.title)) {
                phase.fields.forEach((field) => {
                    const value = Array.isArray(field.userGivenValue) ? field.userGivenValue[0] : field.userGivenValue;
                    if (value === "No") {
                        pendencies.push({
                            title: field.label,
                            value: t('pending_item'),
                            valid: false
                        });
                    }
                });
            }
        });

        return pendencies.length > 0 ? pendencies : null;
    };

    const renderFormPhase = (phase) => {

        const content = phase.fields.map((field, index) => {
            const value = Array.isArray(field.userGivenValue) ? field.userGivenValue[0] : field.userGivenValue;

            return {
                valid: field.isGoodToGo,
                title: field.label,
                value,
            }
        })
        return { title: phase.title, content };
    };

    const mapMeggerContent = (step, stepIndex) => {
        return {
            title: step.title,
            value: {
                reading: { value: step.imageAnalysis?.reading },
                reading_unit: {
                    value: step.imageAnalysis?.reading_unit,
                    type: 'select',
                },
            },
            valid: step.valid,
            index: stepIndex,
        };
    };

    // Only these imageAnalysis keys are user-facing measurements. Everything else
    // (openAIResponse payloads, normalizedValue, valid/operator flags) is internal
    // and must never reach the printed report.
    const ANALYSIS_KEYS = ['reading', 'reading_unit', 'date', 'voltage', 'voltage_unit', 'frequency', 'time', 'current', 'time_count'];
    const isRenderableAnalysisValue = (value) =>
        (typeof value === 'string' || typeof value === 'number') &&
        !/falha na comunica|openai|json de resposta/i.test(String(value));

    const mapHipotContent = (step, stepIndex) => {
        return Object.entries(step.imageAnalysis)
            .map(([key, value]) => {
                if (ANALYSIS_KEYS.includes(key) && isRenderableAnalysisValue(value)) {
                    return {
                        title: t(key) ?? key,
                        value: value,
                        valid: key === 'time_count' ? step.valid : true,
                        index: stepIndex,
                        key,
                    };
                }
                return null;
            })
            .filter(Boolean);
    };

    const renderCameraPhase = (phase) => {
        const content = phase.steps
            .map((step, stepIndex) => {
                if (step.imageAnalysis) {
                    if (isRenderableAnalysisValue(step.imageAnalysis?.reading) && step.imageAnalysis?.reading_unit) {
                        return mapMeggerContent(step, stepIndex);
                    }
                    return mapHipotContent(step, stepIndex);
                }
                return null;
            })
            .filter(Boolean)
            .flat();

        return content.length > 0 ? { title: phase.title, content } : null;
    };

    // SignatureBlock agora tem acesso direto ao t do useTranslation
    const SignatureBlock = ({ title, data }) => (
        <View style={styles.signatureBlock}>
            <Text style={styles.signatureTitle}>{title}</Text>
            <Text style={{ fontFamily: 'DrSugiyama', fontSize: 20, height: 32, marginVertical: 0, textAlign: 'center', color: 'black' }}>
                {data?.sign_name || ''}
            </Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>
                {data ? `${data.reproved ? t('reproved_by') : ''} ${data.name?.toUpperCase() || ' '}` : ' '}
            </Text>
            <Text style={styles.signatureEmail}>
                {data?.email?.toUpperCase() || ' '}
            </Text>
            <Text style={styles.signatureCompany}>
                {data?.company?.toUpperCase() || ' '}
            </Text>
            <Text style={styles.signatureCompany}>
                {data?.date ? moment(data.date).format('MM/DD/YYYY') : ' '}
            </Text>
        </View>
    );

    // Componente principal retornando o PDF
    return (
        <Document key={reportCode}>
            <Page size="A4" style={styles.page}>
                <View style={styles.reportContainer}>
                    <View style={styles.reportHeader}>
                        {logoUrl && <Image src={logoUrl} style={styles.logoOverview} />}
                        <View style={styles.title}>
                            <Text>
                                {report.test[i18n.language].reportName}
                            </Text>
                        </View>
                        <View style={styles.title}>
                            <Text>
                                {reportNumber}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.reportContent}>
                        <ReportTable
                            title={t('general_tab')}
                            content={[
                                { title: t('contractor') + ': ', value: report.employee.properties.find((p) => p.key === 'company').value, valid: true },
                                {
                                    title: t('date') + ':',
                                    // Test execution date — never updatedAt, which moves on any workflow touch (signatures, edits)
                                    value: report.createdAt ? moment(report.createdAt).format('DD/MM/YYYY') : 'N/A',
                                    valid: true,
                                },
                                { title: "Status: ", value: report.status, valid: true },
                                { title: t('location') + ':', value: report.subject?.location?.name, valid: true },
                                { title: t('report_circuit') + ':', value: report.subject?.name, valid: true },
                                { title: t('token') + ':', value: report.token, valid: true },
                                { title: t('attempt') + ':', value: report.attempt, valid: true },
                                { title: t('origin') + ':', value: report.subject.properties.find(p => p.key === 'origin')?.value, valid: true },
                                { title: t('destiny') + ':', value: report.subject.properties.find(p => p.key === 'destiny')?.value, valid: true },
                            ]}
                            columns={3}
                        />

                        {generateContentPhases(report).map((phase, index) => (
                            <ReportTable
                                key={index}
                                title={phase.title}
                                content={phase.content}
                                columns={3}
                            />
                        ))}

                        {report.phase
                            .filter((phase) => phase.type === 'camera' || phase.type === 'evidency')
                            .map((phase) => phase.steps)
                            .flat()
                            .map((step) => step.images)
                            .flat()
                            .length > 0 && (
                                <Evidences
                                    images={report.phase
                                        .filter((phase) => phase.type === 'camera' || phase.type === 'evidency')
                                        .map((phase) => phase.steps)
                                        .flat()
                                        .map((step) =>
                                            step.images.map((image) => ({
                                                url: image.url,
                                                title: step.title,
                                            }))
                                        )
                                        .flat()}
                                />
                            )}

                        {report.report.notes && (
                            <FullWidthSection
                                title={"Notas/Observações"}
                                text={typeof report.report.notes === 'string' ? report.report.notes : ''}
                            />
                        )}

                        <ReportTable
                            title={"Conclusão"}
                            content={[
                                { title: "Status: ", value: report.report.status, valid: true },]}
                        />


                        <View style={styles.reportTable} wrap={false} break>
                            <View style={styles.reportTableTitle}>
                                <Text style={styles.reportTableTitleText}>{t('signatures')}</Text>
                            </View>
                            <View style={styles.signatureSection} wrap={false}>
                                <SignatureBlock
                                    title={t('executor')}
                                    data={{
                                        name: report?.employee?.properties.find((prop) => prop.key === 'name')?.value,
                                        sign_name: formatName(
                                            report?.employee?.properties.find((prop) => prop.key === 'name')?.value
                                        ),
                                        email: report?.employee?.email,
                                        company: report?.employee?.properties.find((prop) => prop.key === 'company')?.value,
                                        date: report.createdAt,
                                    }}
                                />
                                {report.report.signatures?.map((signature, index) => (
                                    <SignatureBlock
                                        key={index}
                                        title={t(`report_${signature.anchor}`)}
                                        data={
                                            signature.role
                                                ? {
                                                    name: signature?.role?.name,
                                                    sign_name: formatName(signature?.role?.name),
                                                    email: signature?.role?.email,
                                                    company: signature?.role?.company,
                                                    reproved: !signature?.approve,
                                                    date: signature?.timestamp,
                                                }
                                                : null
                                        }
                                    />
                                ))}
                            </View>
                        </View>
                    </View>
                </View>
            </Page>

            {generatePendenciesTable(report) && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.reportContainer}>
                        <View style={styles.reportHeader}>
                            {logoUrl && <Image src={logoUrl} style={styles.logoOverview} />}
                            <View style={styles.title}>
                                <Text>{t('pendencies')} - {report.test[i18n.language].reportName}</Text>
                            </View>
                            <View style={styles.title}>
                                <Text>
                                    {reportNumber}
                                </Text>
                            </View>
                        </View>

                        <View style={styles.reportContent}>
                            <ReportTable
                                title={t('pendencies')}
                                content={generatePendenciesTable(report)}
                            />
                        </View>
                    </View>
                </Page>
            )}
        </Document>
    );
};

export default ReportTemplate;
